import { SlotProps, TextFieldProps } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Menu from '@mui/material/Menu';
import TextField from '@mui/material/TextField';
import { DateCalendar } from '@mui/x-date-pickers';
import { formatDate } from 'cleave-zen';
import * as datefns from 'date-fns';
import React from 'react';

import CalendarIcon from '@/assets/icons/calendar.svg?react';

/**
 * Manually formats a string of digits as dd/MM/yyyy without reinterpreting structure.
 * Used during deletion to prevent cleave-zen from corrupting the date structure.
 */
const manualFormat = (digits: string): string => {
  // Apply format: dd/MM/yyyy
  if (digits.length <= 2) {
    return digits;
  }
  if (digits.length <= 4) {
    return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  }
  if (digits.length <= 8) {
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
  }

  // Limit to 8 digits max
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
};

interface Props {
  readonly label: string;
  readonly value: Date | null | undefined;
  readonly format: string;
  readonly reset: React.RefObject<VoidFunction>;
  readonly disableFuture?: boolean;
  readonly shouldDisableDate?: (date: Date) => boolean;
  readonly error?: boolean;
  readonly helperText?: string;

  onChange(date: Date | null): void;
}

/**
 * DatePicker component that implements a hybrid controlled/uncontrolled pattern.
 *
 * This component maintains both controlled and uncontrolled state:
 * - Controlled: The main date value is controlled through props.value and props.onChange
 * - Uncontrolled: Internal string representation of the date is managed locally
 *
 * While this is generally considered an anti-pattern, it's currently implemented this way to:
 * 1. Allow proper cursor position management during manual date input
 * 2. Handle incomplete date strings during user typing
 * 3. Maintain a formatted string representation independently from the Date object
 *
 * Note: The author (Iharob Al Asimi) acknowledges this is not the most elegant solution
 * and couldn't come up with a better approach at the time. The component works as intended
 * but the implementation could be improved in future iterations.
 */
export const DatePicker: React.FC<Props> = (props: Props): React.ReactElement => {
  const {
    label,
    value,
    format,
    reset,
    disableFuture,
    shouldDisableDate,
    error,
    helperText,
    onChange,
  } = props;

  const [internalValue, setInternalValue] = React.useState<string>(
    value ? datefns.format(value, format) : '',
  );
  const [expanded, setExpanded] = React.useState<boolean>(false);

  const inputRef = React.useRef<HTMLInputElement>(null);

  const handleChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const input = event.target;
      const rawValue = input.value;
      const currentCaretPosition = input.selectionStart ?? 0;

      // Count how many data characters (non-delimiters) are before the cursor
      // This gives us the semantic position independent of formatting
      let dataCharsBeforeCursor = 0;

      for (let i = 0; i < currentCaretPosition && i < rawValue.length; i++) {
        if (rawValue[i] !== '/') {
          dataCharsBeforeCursor++;
        }
      }

      // Strip non-digits to detect if user is deleting or typing
      const digitsOnly = rawValue.replace(/[^\d]/g, '');
      const previousDigitsOnly = internalValue.replace(/[^\d]/g, '');

      // Detect if user is deleting content
      const isDeleting = digitsOnly.length < previousDigitsOnly.length;

      let formatted: string;

      if (isDeleting) {
        // During deletion, use manual formatting to prevent cleave-zen
        // from reinterpreting the date structure
        formatted = manualFormat(digitsOnly);
      } else {
        // During typing, use cleave-zen for validation and smart formatting
        formatted = formatDate(rawValue, {
          delimiterLazyShow: true,
        });
      }

      const date = datefns.parse(formatted, format, 0);
      if (datefns.isValid(date)) {
        onChange(date);
      } else {
        onChange(null);
      }

      // Find the position in the formatted string that corresponds to
      // the same number of data characters
      let newPosition = 0;
      let dataCharsCount = 0;

      while (dataCharsCount < dataCharsBeforeCursor && newPosition < formatted.length) {
        if (formatted[newPosition] !== '/') {
          dataCharsCount++;
        }

        newPosition++;
      }

      setInternalValue(formatted);

      // Need to set caret position after React rerender
      requestAnimationFrame((): void => {
        if (input) {
          input.setSelectionRange(newPosition, newPosition);
        }
      });
    },
    [internalValue, format, onChange],
  );

  const toggleExpanded = React.useCallback((): void => {
    setExpanded((expanded: boolean): boolean => !expanded);
  }, []);

  const InputProps = React.useMemo(
    (): SlotProps<React.ElementType<TextFieldProps>, any, any> => ({
      input: {
        endAdornment: (
          <InputAdornment position="end">
            <IconButton onClick={toggleExpanded}>
              <CalendarIcon fill="current" width={24} height={24} />
            </IconButton>
          </InputAdornment>
        ),
        ref: inputRef,
      },
    }),
    [toggleExpanded],
  );

  const onDatePicked = React.useCallback(
    (value: Date): void => {
      setInternalValue(datefns.format(value, format));
      onChange(value);
    },
    [format, onChange],
  );

  const placeholder = React.useMemo((): string => format.toUpperCase(), [format]);

  React.useEffect((): void => {
    reset.current = (): void => {
      setInternalValue('');
      onChange(null);
      setExpanded(false);
    };
  }, [onChange, reset]);

  return (
    <>
      <TextField
        label={label}
        value={internalValue}
        onChange={handleChange}
        placeholder={placeholder}
        slotProps={InputProps}
        error={error}
        helperText={helperText}
      />
      <Menu open={expanded} onClose={toggleExpanded} anchorEl={inputRef.current}>
        <DateCalendar
          value={value}
          onChange={onDatePicked}
          disableFuture={disableFuture}
          shouldDisableDate={shouldDisableDate}
        />
      </Menu>
    </>
  );
};
